import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import {
  getDailyPollById,
  updateDailyPoll,
} from "../../services/dailyPoll.service";
import { Loading } from "../../components/Loading";
import { ErrorPageDetails } from "../../components/ErrorPageDetails";
import DetailsLabel from "../../components/DetailsLabel";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import DailyPollForm, { buildDailyPollInitialValues } from "./DailyPollForm";

const DailyPollView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const pollFromState =
    location.state?.poll && String(location.state.poll.id) === String(id)
      ? location.state.poll
      : null;

  const {
    data: poll,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["daily-poll", id],
    queryFn: () => getDailyPollById(id),
    initialData: pollFromState,
  });

  const [isEditing, setIsEditing] = useState(false);

  const { mutate: updatePoll, isPending: isUpdatePending } = useMutation({
    mutationKey: ["updateDailyPoll", id],
    mutationFn: (payload) => updateDailyPoll(id, payload),
    onSuccess: () => {
      alert("Daily poll updated");
      setIsEditing(false);
      queryClient.invalidateQueries(["daily-poll", id]);
      queryClient.invalidateQueries(["daily-polls"]);
    },
    onError: (error) => {
      alert(`Error updating daily poll: ${error || "Unknown error"}`);
    },
  });

  const totalVotes = useMemo(() => {
    return poll?.totalVotes ?? 0;
  }, [poll]);

  const optionResults = useMemo(() => {
    const options = poll?.options ?? [];
    return options.map((option) => {
      const count =
        option?.voteCount ?? option?.votes ?? option?.totalVotes ?? 0;
      const percent = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
      return {
        id: option?.id,
        text: option?.text,
        colorHex: option?.colorHex,
        count,
        percent,
      };
    });
  }, [poll, totalVotes]);

  const hasVotes = useMemo(() => {
    if (totalVotes > 0) {
      return true;
    }
    return (optionResults || []).some((option) => option.count > 0);
  }, [optionResults, totalVotes]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !poll) {
    return <ErrorPageDetails text={"Error loading Daily Poll"} />;
  }

  return (
    <Container component="main">
      <Box sx={{ mb: 3 }}>
        <Typography component="h1" variant="h4">
          Daily Poll Results
        </Typography>

        {isEditing ? (
          <Box sx={{ mt: 2 }}>
            <DailyPollForm
              initialValues={buildDailyPollInitialValues(poll)}
              submitLabel="Save Changes"
              onSubmit={(payload) => updatePoll(payload)}
              onCancel={() => setIsEditing(false)}
              isSubmitting={isUpdatePending}
              optionsLocked={hasVotes}
            />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <DetailsLabel label={"Question"} value={poll.question || "N/A"} />

            <DetailsLabel label={"Type"} value={poll.type || "N/A"} />

            <DetailsLabel
              label={"Publish At"}
              value={
                poll.publishAt
                  ? format(new Date(poll.publishAt), DATE_TIME_FORMAT)
                  : "N/A"
              }
            />
            <DetailsLabel
              label={"Expires At"}
              value={
                poll.expiresAt
                  ? format(new Date(poll.expiresAt), DATE_TIME_FORMAT)
                  : "N/A"
              }
            />

            <DetailsLabel
              label={"Status"}
              value={poll.isActive ? "Active" : "Inactive"}
            />
            <DetailsLabel label={"Total Votes"} value={totalVotes} />

            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button variant="contained" onClick={() => setIsEditing(true)}>
                Edit Daily Poll
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Option</TableCell>

            <TableCell>Votes</TableCell>

            <TableCell>Percent</TableCell>

            <TableCell>Color</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {optionResults.length ? (
            optionResults.map((option) => (
              <TableRow key={option.id}>
                <TableCell>{option.text || option.id}</TableCell>

                <TableCell>{option.count}</TableCell>

                <TableCell>{option.percent.toFixed(1)}%</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "4px",
                      backgroundColor: option.colorHex || "#000000",
                    }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography variant="subtitle1" color="textSecondary">
                  No results available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate("/daily-polls")}>
          Back to Daily Polls
        </Button>
      </Box>
    </Container>
  );
};

export default DailyPollView;
